import { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  SxProps,
  Theme,
  Typography,
} from '@mui/material';
import { updateMeta } from '../../../redux/meta';
import { useAppDispatch } from '../../../pages/assessment';
import { AssessmentButtons } from '../shared';
import { theme } from '../../../styles/theme';
import { useExperimentation } from '../../../experimentation/client';
import { ModalDialog } from '../../reusable';
import { WhatsTalkiatry } from '../../app/menu/whats-talkiatry';
import { WhatsPsychiatry } from '../../app/menu/whats-psychiatry';

type SummaryCardVariantName = 'saffron' | 'pistachio' | 'sky';

type PropTypes = {
  variant: SummaryCardVariantName;
  index?: number;
  label: string;
  desc: string;
};

const BG_IMG_VARIANTS: Record<SummaryCardVariantName, SxProps<Theme>> = {
  saffron: {
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      bottom: 0,
      width: '24px',
      backgroundImage: 'url("/brain-bottom-saffron-monochrome-expanded.svg")',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: '20% 40%',
      backgroundColor: theme.palette.secondary.main,
      backgroundSize: '550px',
    },
  },
  pistachio: {
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      bottom: 0,
      width: '24px',
      backgroundImage: 'url("/human-pistachio-expanded.svg")',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: '15% 45%',
      backgroundColor: 'pistachio.main',
      backgroundSize: '450px',
    },
  },
  sky: {
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      bottom: 0,
      width: '24px',
      backgroundImage: 'url("/pathway-c-sky-duochrome-expanded.svg")',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: '10% 60%',
      backgroundColor: 'sky.main',
      backgroundSize: '2000px',
    },
  },
};

const SummaryCard = ({ variant, index, label, desc }: PropTypes) => (
  <Card
    sx={{
      mb: 2,
      pl: 3,
      position: 'relative',
      ...BG_IMG_VARIANTS[variant],
    }}
  >
    <CardContent sx={{ mx: 1, mt: 1 }}>
      <Box sx={{ display: 'flex' }}>
        {index > 0 ? (
          <Box
            sx={{
              width: 26,
              height: 26,
              backgroundColor: 'primary.main',
              color: 'white',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: 'subtitle1',
              mr: 1,
            }}
          >
            {index}
          </Box>
        ) : null}
        <Typography variant="subtitle1" sx={{ mb: 1, color: 'primary.main' }}>
          {label}
        </Typography>
      </Box>
      <Typography variant="body1">{desc}</Typography>
    </CardContent>
  </Card>
);

const IntroScreenFour = () => {
  const dispatch = useAppDispatch();

  const [shouldShowAbTestScreens] = useExperimentation({
    flagName: 'ab_test_intro_screens',
  });

  const [isContinuing, setIsContinuing] = useState(false);
  const onClick = () => {
    setIsContinuing(true);
    dispatch(updateMeta());
  };

  const cardData: PropTypes[] = [
    {
      variant: 'saffron',
      index: 1,
      label: shouldShowAbTestScreens
        ? 'Tell us about you'
        : 'Insurance and location check',
      desc: shouldShowAbTestScreens
        ? "Take 10-minutes to let us know why you're here and what you're looking for."
        : "We'll ask some basic questions to see if we're a good fit for you.",
    },
    {
      variant: 'pistachio',
      index: 2,
      label: shouldShowAbTestScreens
        ? 'Explore your matches'
        : 'Clinical questions for matching',
      desc: shouldShowAbTestScreens
        ? "We'll show you bios of psychiatrists who fit your needs."
        : "Based on your needs, we'll match you with a clinician who can help.",
    },
    {
      variant: 'sky',
      index: 3,
      label: shouldShowAbTestScreens
        ? 'Schedule a visit'
        : 'Book a virtual visit with a doctor',
      desc: shouldShowAbTestScreens
        ? 'Find a time that works for you and get the care you deserve.'
        : 'Self-schedule a virtual visit with the clinician you choose.',
    },
  ];

  return (
    <Box>
      {cardData.map((data, index) => (
        <SummaryCard
          key={index}
          variant={data.variant}
          index={data.index}
          label={data.label}
          desc={data.desc}
        />
      ))}
      <AssessmentButtons
        primary={{
          children: shouldShowAbTestScreens
            ? 'Start our short assessment'
            : 'Get Started',
          disabled: isContinuing,
          loading: isContinuing,
          onClick,
        }}
      />
      {shouldShowAbTestScreens && (
        <>
          <ModalDialog title="What's Talkiatry?">
            <WhatsTalkiatry color="primary.main" />
          </ModalDialog>
          <ModalDialog title="What's psychiatry?">
            <WhatsPsychiatry color="primary.main" />
          </ModalDialog>
        </>
      )}
    </Box>
  );
};

export default IntroScreenFour;
